import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';

// Project types for VRP scenarios
const PROJECT_TYPES = [
  'Delivery & Logistics',
  'Field Service',
  'Transportation',
  'Supply Chain',
  'Food Delivery',
  'Healthcare Services',
  'Emergency Services',
  'Waste Management',
  'Construction',
  'Retail Distribution',
  'E-commerce Fulfillment',
  'Other'
];

// Industry sectors
const INDUSTRY_SECTORS = [
  'Retail & E-commerce',
  'Food & Beverage',
  'Healthcare & Pharmaceutical',
  'Manufacturing',
  'Transportation & Logistics',
  'Construction & Engineering',
  'Government & Public Services',
  'Energy & Utilities',
  'Technology',
  'Agriculture',
  'Financial Services',
  'Education',
  'Other'
];

// Geographic regions
const GEOGRAPHIC_REGIONS = [
  'North America',
  'South America', 
  'Europe',
  'Asia-Pacific',
  'Middle East',
  'Africa',
  'Global',
  'Other'
];

// Project priority levels
const PRIORITY_LEVELS = [
  { value: 'low', label: 'Low Priority' },
  { value: 'medium', label: 'Medium Priority' },
  { value: 'high', label: 'High Priority' },
  { value: 'critical', label: 'Critical Priority' }
];

// Common currency options
const CURRENCY_OPTIONS = [
  { code: 'USD', label: 'USD - US Dollar' },
  { code: 'EUR', label: 'EUR - Euro' },
  { code: 'GBP', label: 'GBP - British Pound' },
  { code: 'ZAR', label: 'ZAR - South African Rand' },
  { code: 'JPY', label: 'JPY - Japanese Yen' },
  { code: 'CAD', label: 'CAD - Canadian Dollar' },
  { code: 'AUD', label: 'AUD - Australian Dollar' },
  { code: 'CHF', label: 'CHF - Swiss Franc' },
  { code: 'CNY', label: 'CNY - Chinese Yuan' },
  { code: 'SEK', label: 'SEK - Swedish Krona' },
  { code: 'INR', label: 'INR - Indian Rupee' },
  { code: 'BRL', label: 'BRL - Brazilian Real' }
];

interface ProjectFormData {
  name: string;
  description?: string;
  currency?: string;
  // Enhanced project management fields
  project_type?: string;
  industry?: string;
  geographic_region?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  estimated_vehicles?: number;
  estimated_jobs?: number;
  planning_horizon_days?: number;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  tags?: string[];
  notes?: string;
}

// CSS-in-JS styles matching the VRP system
const styles = {
  // CSS Variables matching mockup
  vars: {
    '--vrp-primary': '#065f46',
    '--vrp-secondary': '#7dd3fc',
    '--vrp-accent': '#10b981',
    '--vrp-navy': '#1e293b',
    '--surface-primary': '#ffffff',
    '--surface-secondary': '#f8fafc',
    '--surface-tertiary': '#f1f5f9',
    '--text-primary': '#0f172a',
    '--text-secondary': '#475569',
    '--text-tertiary': '#94a3b8',
    '--border-subtle': '#e2e8f0',
    '--text-xs': '0.75rem',
    '--text-sm': '0.875rem',
    '--text-base': '1rem',
    '--text-lg': '1.125rem',
    '--text-xl': '1.25rem',
    '--text-2xl': '1.5rem',
    '--weight-normal': '400',
    '--weight-medium': '500',
    '--weight-semibold': '600',
    '--weight-bold': '700',
    '--space-1': '0.25rem',
    '--space-2': '0.5rem',
    '--space-3': '0.75rem',
    '--space-4': '1rem',
    '--space-6': '1.5rem',
    '--space-8': '2rem',
    '--radius-md': '0.375rem',
    '--radius-lg': '0.5rem',
    '--radius-xl': '0.75rem',
    '--radius-2xl': '1rem',
    '--radius-full': '9999px',
    '--duration-fast': '0.15s',
    '--duration-normal': '0.25s',
    '--ease-out-quart': 'cubic-bezier(0.25, 1, 0.5, 1)',
  },

  formContainer: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: 'var(--space-6)',
    background: 'var(--surface-secondary)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
  },

  formGroup: {
    marginBottom: 'var(--space-6)',
  },

  label: {
    display: 'block',
    marginBottom: 'var(--space-2)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-semibold)',
    color: 'var(--text-primary)',
  },

  input: {
    width: '100%',
    padding: 'var(--space-3) var(--space-4)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    fontSize: 'var(--text-sm)',
    transition: 'border-color var(--duration-fast) var(--ease-out-quart)',
    outline: 'none',
  },

  textarea: {
    width: '100%',
    padding: 'var(--space-3) var(--space-4)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    fontSize: 'var(--text-sm)',
    minHeight: '100px',
    resize: 'vertical' as const,
    transition: 'border-color var(--duration-fast) var(--ease-out-quart)',
    outline: 'none',
  },

  buttonGroup: {
    display: 'flex',
    gap: 'var(--space-3)',
    justifyContent: 'flex-end',
    marginTop: 'var(--space-8)',
  },

  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-3) var(--space-6)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-medium)',
    transition: 'all var(--duration-fast) var(--ease-out-quart)',
    cursor: 'pointer',
    border: 'none',
    textDecoration: 'none',
  },

  btnPrimary: {
    background: 'var(--vrp-primary)',
    color: 'white',
  },

  btnSecondary: {
    background: 'var(--surface-tertiary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-subtle)',
  },
};

export const ProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCurrentProject } = useProject();
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    currency: 'USD',
    project_type: '',
    industry: '',
    geographic_region: '',
    priority: 'medium',
    estimated_vehicles: 5,
    estimated_jobs: 100,
    planning_horizon_days: 7,
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    tags: [],
    notes: '',
  });

  // Apply CSS variables
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    const cssVariables = Object.entries(styles.vars)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n    ');
    
    styleElement.textContent = `
      .project-form-scope {
        ${cssVariables}
      }
      
      .project-form-scope input:focus {
        border-color: var(--vrp-primary);
        box-shadow: 0 0 0 3px rgba(6, 95, 70, 0.1);
      }
      
      .project-form-scope textarea:focus {
        border-color: var(--vrp-primary);
        box-shadow: 0 0 0 3px rgba(6, 95, 70, 0.1);
      }
      
      .project-form-scope button:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      
      .project-form-scope .btn-primary:hover:not(:disabled) {
        background: color-mix(in srgb, var(--vrp-primary) 90%, black) !important;
      }
      
      .project-form-scope .btn-secondary:hover:not(:disabled) {
        background: var(--surface-secondary) !important;
        border-color: var(--vrp-primary) !important;
      }
    `;
    
    document.head.appendChild(styleElement);

    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  const createProjectMutation = useMutation({
    mutationFn: (data: ProjectFormData) => apiClient.createProject(data),
    onSuccess: (newProject) => {
      console.log('Project created successfully:', newProject);
      setCurrentProject(newProject);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/projects');
    },
    onError: (error) => {
      console.error('Failed to create project:', error);
      alert('Failed to create project. Please try again.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      alert('Project name is required');
      return;
    }

    if (!formData.project_type) {
      alert('Project type is required');
      return;
    }

    if (!formData.industry) {
      alert('Industry sector is required');
      return;
    }

    if (!formData.geographic_region) {
      alert('Geographic region is required');
      return;
    }

    console.log('Creating project:', formData);
    createProjectMutation.mutate(formData);
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  return (
    <ProfessionalLayout
      pageTitle="Create New VRP Project"
      pageSubtitle="Complete project setup with business details, planning parameters, and contact information"
      showTabs={false}
      actionButtons={
        <button 
          style={{ ...styles.btn, ...styles.btnSecondary }}
          onClick={handleCancel}
        >
          Back to Projects
        </button>
      }
    >
      <div className="project-form-scope">
        <form onSubmit={handleSubmit} style={styles.formContainer}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Project Name *
            </label>
            <input
              type="text"
              style={styles.input}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter project name (e.g., Delivery Route Optimization)"
              required
              autoFocus
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Description
            </label>
            <textarea
              style={styles.textarea}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your VRP project goals and requirements..."
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Currency *
            </label>
            <select
              style={styles.input}
              value={formData.currency || 'USD'}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              required
            >
              {CURRENCY_OPTIONS.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.label}
                </option>
              ))}
            </select>
          </div>

          {/* Project Classification Section */}
          <div style={{ ...styles.formGroup, marginTop: 'var(--space-8)' }}>
            <h3 style={{ 
              fontSize: 'var(--text-lg)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--vrp-primary)',
              marginBottom: 'var(--space-4)',
              borderBottom: '2px solid var(--border-subtle)',
              paddingBottom: 'var(--space-2)'
            }}>
              Project Classification
            </h3>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Project Type *
            </label>
            <select
              style={styles.input}
              value={formData.project_type || ''}
              onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
              required
            >
              <option value="">Select project type...</option>
              {PROJECT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Industry Sector *
            </label>
            <select
              style={styles.input}
              value={formData.industry || ''}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              required
            >
              <option value="">Select industry...</option>
              {INDUSTRY_SECTORS.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Geographic Region *
            </label>
            <select
              style={styles.input}
              value={formData.geographic_region || ''}
              onChange={(e) => setFormData({ ...formData, geographic_region: e.target.value })}
              required
            >
              <option value="">Select region...</option>
              {GEOGRAPHIC_REGIONS.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Priority Level
            </label>
            <select
              style={styles.input}
              value={formData.priority || 'medium'}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'low' | 'medium' | 'high' | 'critical' })}
            >
              {PRIORITY_LEVELS.map((priority) => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>

          {/* Planning & Scale Section */}
          <div style={{ ...styles.formGroup, marginTop: 'var(--space-8)' }}>
            <h3 style={{ 
              fontSize: 'var(--text-lg)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--vrp-primary)',
              marginBottom: 'var(--space-4)',
              borderBottom: '2px solid var(--border-subtle)',
              paddingBottom: 'var(--space-2)'
            }}>
              Planning & Scale
            </h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
            <div>
              <label style={styles.label}>
                Estimated Vehicles
              </label>
              <input
                type="number"
                style={styles.input}
                value={formData.estimated_vehicles || 5}
                onChange={(e) => setFormData({ ...formData, estimated_vehicles: parseInt(e.target.value) || 5 })}
                min="1"
                max="1000"
                placeholder="5"
              />
            </div>

            <div>
              <label style={styles.label}>
                Estimated Jobs/Deliveries
              </label>
              <input
                type="number"
                style={styles.input}
                value={formData.estimated_jobs || 100}
                onChange={(e) => setFormData({ ...formData, estimated_jobs: parseInt(e.target.value) || 100 })}
                min="1"
                max="50000"
                placeholder="100"
              />
            </div>

            <div>
              <label style={styles.label}>
                Planning Horizon (Days)
              </label>
              <input
                type="number"
                style={styles.input}
                value={formData.planning_horizon_days || 7}
                onChange={(e) => setFormData({ ...formData, planning_horizon_days: parseInt(e.target.value) || 7 })}
                min="1"
                max="365"
                placeholder="7"
              />
            </div>
          </div>

          {/* Contact Information Section */}
          <div style={{ ...styles.formGroup, marginTop: 'var(--space-8)' }}>
            <h3 style={{ 
              fontSize: 'var(--text-lg)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--vrp-primary)',
              marginBottom: 'var(--space-4)',
              borderBottom: '2px solid var(--border-subtle)',
              paddingBottom: 'var(--space-2)'
            }}>
              Contact Information
            </h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
            <div>
              <label style={styles.label}>
                Contact Person
              </label>
              <input
                type="text"
                style={styles.input}
                value={formData.contact_person || ''}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="Project manager name"
              />
            </div>

            <div>
              <label style={styles.label}>
                Contact Email
              </label>
              <input
                type="email"
                style={styles.input}
                value={formData.contact_email || ''}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="contact@company.com"
              />
            </div>

            <div>
              <label style={styles.label}>
                Contact Phone
              </label>
              <input
                type="tel"
                style={styles.input}
                value={formData.contact_phone || ''}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          {/* Additional Information Section */}
          <div style={{ ...styles.formGroup, marginTop: 'var(--space-8)' }}>
            <h3 style={{ 
              fontSize: 'var(--text-lg)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--vrp-primary)',
              marginBottom: 'var(--space-4)',
              borderBottom: '2px solid var(--border-subtle)',
              paddingBottom: 'var(--space-2)'
            }}>
              Additional Information
            </h3>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Tags
            </label>
            <input
              type="text"
              style={styles.input}
              placeholder="Enter comma-separated tags (e.g., urgent, pilot-project, cost-optimization)"
              onChange={(e) => {
                const tagString = e.target.value;
                const tags = tagString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
                setFormData({ ...formData, tags });
              }}
            />
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 'var(--space-1)' }}>
              Add tags to categorize and search for projects
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Additional Notes
            </label>
            <textarea
              style={styles.textarea}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Special requirements, constraints, or other important information..."
              rows={4}
            />
          </div>

          <div style={styles.buttonGroup}>
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnSecondary }}
              onClick={handleCancel}
              disabled={createProjectMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ 
                ...styles.btn, 
                ...styles.btnPrimary,
                opacity: (formData.name.trim() && formData.project_type && formData.industry && formData.geographic_region && !createProjectMutation.isPending) ? 1 : 0.5,
                cursor: (formData.name.trim() && formData.project_type && formData.industry && formData.geographic_region && !createProjectMutation.isPending) ? 'pointer' : 'not-allowed'
              }}
              className="btn-primary"
              disabled={!formData.name.trim() || !formData.project_type || !formData.industry || !formData.geographic_region || createProjectMutation.isPending}
            >
              {createProjectMutation.isPending ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </ProfessionalLayout>
  );
};

export default ProjectForm;